import React from 'react';
import {
  TrendingUp,
  AlertTriangle,
  Heart,
  BarChart3,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import { useAuthInfo } from '@propelauth/react';
import { ChatInput } from './ChatInput';

interface ChatMainContentProps {
  onStartChat: (message?: string) => void;
  isReady?: boolean;
}

const suggestions = [
  {
    icon: TrendingUp,
    title: 'Trend Analysis',
    description: 'Is @Acme Corp trending up or down, and why?',
  },
  {
    icon: AlertTriangle,
    title: 'Risk Analysis',
    description: 'Show me top accounts at risk this month',
  },
  {
    icon: Heart,
    title: 'Health Score Review',
    description: 'Generate health scores for my portfolio',
  },
  {
    icon: BarChart3,
    title: 'Top Active Accounts',
    description: 'Show me top 10 active accounts this week',
  },
];

export const ChatMainContent: React.FC<ChatMainContentProps> = ({ onStartChat, isReady = false }) => {
  const authInfo = useAuthInfo();
  const userName = authInfo?.user?.firstName || authInfo?.user?.email?.split('@')[0] || 'there';

  const handleSendMessage = (message: string | FormData) => {
    const messageText = message instanceof FormData
      ? message.get('message') as string
      : message;
    onStartChat(messageText);
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      {/* Main content area */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center px-6">
        {/* ORA Icon & Greeting */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-muted-foreground" />
            </div>
          </div>

          <h1 className="text-2xl font-semibold text-foreground mb-2">
            How can I help, <span className="text-primary">{userName}</span>?
          </h1>
        </div>

        {/* Suggestions Section */}
        <div className="w-full max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-sm font-medium text-muted-foreground">Try asking me</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.title}
                type="button"
                onClick={() => {
                  if (isReady) {
                    onStartChat(suggestion.description);
                  }
                }}
                className="group bg-card p-4 rounded-lg border border-border text-left hover:border-border-chip hover:bg-muted transition-all duration-150"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <suggestion.icon className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors duration-150" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm text-foreground mb-0.5">
                      {suggestion.title}
                    </h3>
                    <p className="text-xs text-muted-foreground truncate">
                      {suggestion.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <p className="text-center text-[11px] text-muted-foreground mt-5">
            Click any suggestion or type your own question to get started
          </p>
        </div>
      </div>

      {/* Chat Input at Bottom */}
      <div className="flex-shrink-0 border-t border-border">
        <ChatInput
          onSendMessage={handleSendMessage}
          isReady={isReady}
          placeholder="Ask about your accounts, draft emails, create reports..."
        />
      </div>
    </div>
  );
};

export default ChatMainContent;
